<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <payment-wizard
      v-if="paymentWizardVisible"
      :mode="mode"
      :prices="paymentWizardPrices"
      @close="closePaymentWizard"
    />
    <template #actions>
      <v-btn
        v-if="saveButtonVisible"
        class="mr-2"
        color="success"
        :disabled="!saveButtonEnabled"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        {{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <validation-observer ref="observer">
        <v-form
          ref="form"
          lazy-validation
          :disabled="!isBillingLoaded || !isBillingAllowed"
        >
          <v-row justify="center">
            <v-col
              cols="12"
              md="8"
            >
              <v-alert
                v-if="alertInfo"
                class="mb-4"
                dense
                border="left"
                prominent
                :type="alertInfo.type"
                :color="alertInfo.color"
              >
                <v-row align="center">
                  <v-col class="grow font-weight-medium">
                    {{ alertInfo.message }}
                  </v-col>
                  <v-col class="shrink">
                    <template v-for="(btn, i) in alertInfo.buttons">
                      <v-btn
                        :key="i"
                        :color="btn.color"
                        @click="btn.action()"
                      >
                        {{ btn.text }}
                      </v-btn>
                    </template>
                  </v-col>
                </v-row>
              </v-alert>
              <v-row>
                <v-col
                  cols="12"
                  class="pt-8"
                >
                  <validated-text-field
                    v-model="sender"
                    rules="required|alpha_num|min:2|max:11"
                    :label="$t('label.sender_id')"
                    :hint="senderHint"
                    persistent-hint
                  />
                </v-col>

                <row-form-divider />
                <v-col
                  cols="12"
                  class="py-0"
                >
                  <section-header :title="$t('label.sms_credits')" />
                </v-col>
                <v-col
                  cols="12"
                  class="text--primary pb-0 mb-0"
                >
                  <sms-balance-chart :balance="balance" />
                  <p class="text-center text-caption white-space-preline">
                    {{ $t('label.hint_sms_credits') }}
                  </p>
                </v-col>
                <!-- Billing -->
                <template v-if="isBillingSMSPageFull && isBillingAllowed">
                  <row-form-divider />
                  <v-col
                    cols="12"
                    sm="6"
                    class="d-flex py-0 pt-1 align-center"
                  >
                    <v-checkbox
                      :input-value="autorenewEnabled"
                      :label="$t('label.auto_renew_credits')"
                      @click.stop="autorenewChanged(!autorenewEnabled)"
                    />
                    <v-chip
                      class="ml-2"
                      x-small
                      color="success"
                    >
                      {{ $t('label.recommended') }}
                    </v-chip>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-select
                      v-if="autorenewEnabled"
                      v-model="autorenewPriceID"
                      class="pt-0"
                      :items="pricesInfo"
                      hide-details
                    />
                  </v-col>
                  <template v-if="!autorenewEnabled">
                    <v-col
                      cols="6"
                      class="pt-0 pb-2"
                    >
                      <v-checkbox
                        v-model="balanceNotificationEnabled"
                        :label="$t('label.sms_notify_when_credits_below')"
                      />
                    </v-col>
                    <v-col
                      cols="6"
                      class="py-2"
                    >
                      <validated-text-field
                        v-model.number="balanceNotificationThreshold"
                        rules="required|integer|min_value:0|max_value:1000"
                        :label="$t('label.threshold')"
                        :disabled="!balanceNotificationEnabled"
                        type="integer"
                      />
                    </v-col>
                  </template>
                  <v-col
                    v-if="autorenewEnabled"
                    cols="12"
                    class="py-0 my-0"
                  >
                    <p class="text-center text-caption">
                      {{ $t('label.hint_sms_credits_autorenew') }}
                    </p>
                  </v-col>
                  <template v-if="!autorenewEnabled">
                    <row-form-divider />
                    <v-col
                      cols="12"
                      class="py-0"
                    >
                      <section-header :title="$t('label.sms_purchase_credits')" />
                    </v-col>

                    <v-col
                      cols="12"
                      class="pt-0 pb-2 d-flex align-center"
                    >
                      <v-select
                        v-model="selectedPriceID"
                        :items="pricesInfo"
                        hide-details
                      />
                    </v-col>
                  </template>
                  <template v-if="isInitialOrder || !autorenewEnabled">
                    <v-col
                      cols="12"
                      class="pb-5"
                    >
                      <v-btn
                        block
                        color="success"
                        :disabled="!isBillingLoaded"
                        @click="startPayment"
                      >
                        <v-icon class="mr-2">
                          mdi-credit-card
                        </v-icon> {{ purchaseButtonTitle }}
                      </v-btn>
                      <i18n
                        path="label.billing_all_prices_are_in"
                        tag="p"
                        class="text-secondary text-center pt-2"
                      >
                        <currency-picker
                          v-if="currencySelectorEnabled"
                          :value="selectedCurrency"
                          @input="setCurrency"
                        />
                        <span v-else>
                          {{ currencyText }}
                        </span>
                      </i18n>
                    </v-col>
                  </template>
                </template>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import PaymentWizard from '@/components/views/settings/billing/PaymentWizard.vue';
import SectionHeader from '@/components/forms/SectionHeader.vue';
import SmsBalanceChart from '@/components/views/settings/SmsBalanceChart.vue';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import BillingStore from '@/store/billing-store';
import { performAction, performSaveAction } from '@/services/vue-utils';
import {
  Currency,
  Customer,
  InvoiceStatus,
  Price, Product, SMSProduct,
} from '@/api/api-billing';
import {
  AlertInfo, billingCurrencyText, priceText, pricesForProductAndCurrency,
} from '@/services/billing-utils';
import { ValidationObserver } from 'vee-validate';
import UnsavedGuard from '@/mixins/unsaved-guard';
import CurrencyPicker from '@/components/pickers/CurrencyPicker.vue';
import { PaymentWizardMode } from '@/services/payment-wizard-utils';

type ListInfo<T> = { text: string, value: T, divider?: boolean }[]

export default Vue.extend({
  name: 'Sms',
  components: {
    SettingsForm, SectionHeader, SmsBalanceChart, PaymentWizard, CurrencyPicker,
  },
  mixins: [UnsavedGuard, SettingsBreadcrumbs],
  data() {
    return {
      breadcrumbs: [],

      // balance: 0,
      sender: '',
      autorenewPriceID: null as string | null,
      balanceNotificationThreshold: 100,
      balanceNotificationEnabled: true,

      selectedPriceID: null as string | null,
      paymentWizardPrices: [] as Price[],
      paymentWizardVisible: false,

      selectedCurrency: Currency.EUR as string,
    };
  },
  computed: {
    mode(): PaymentWizardMode {
      return PaymentWizardMode.Prices;
    },
    senderHint(): string {
      return `${this.sender.length}/11`;
    },
    purchaseButtonTitle(): string {
      let priceID = this.selectedPriceID;
      if (this.isInitialOrder) {
        // initial payment with autorenew price id if enabled
        if (this.autorenewPriceID) priceID = this.autorenewPriceID;
      }

      const priceInfo = this.pricesInfo.find((pi) => pi.value === priceID);
      return `${this.$tc('button.purchase')} ${priceInfo?.text ?? ''}`;
    },
    isBillingSMSPageFull(): boolean {
      return this.$tstore.getters.isBillingSMSPageFull as boolean;
    },
    isBillingAllowed(): boolean {
      return this.$tstore.getters.isBillingAllowed as boolean;
    },
    isInitialOrder(): boolean {
      return this.smsProduct === null || !this.smsProduct.latestInvoiceStatus || !this.customer;
    },
    prices(): Price[] {
      const product = this.$tstore.getters.billingSMSPriceProduct as Product | null;
      if (!product) return [];
      const prices = pricesForProductAndCurrency(product, this.selectedCurrency);
      return prices;
    },
    pricesInfo(): ListInfo<string> {
      return this.prices.map((p) => ({ text: priceText(p), value: p.ID! }));
    },
    isBillingLoaded(): boolean {
      return this.$tstore.getters.isBillingLoaded === true;
    },
    balance(): number { return this.smsProduct?.balance ?? 0; },
    autorenewEnabled(): boolean {
      console.log('autorenewEnabled: ', this.autorenewPriceID);
      return this.autorenewPriceID !== null;
    },
    isChanged(): boolean {
      if (this.isInitialOrder) return false; // for unsaved guard

      return this.sender.trim() !== (this.smsProduct?.sender ?? '')
        || this.autorenewPriceID !== (this.smsProduct?.autorenewPriceID ?? null)
        || this.balanceNotificationThreshold !== this.smsProduct?.balanceNotificationThreshold
        || this.balanceNotificationEnabled !== (this.smsProduct?.balanceNotificationEnabled ?? false);
    },
    saveButtonVisible(): boolean {
      return this.isBillingLoaded && !this.isInitialOrder;
    },
    saveButtonEnabled(): boolean {
      return this.isBillingLoaded && !this.isInitialOrder && this.isChanged;
    },
    // smsApproved(): boolean {
    //   return this.smsProduct !== null;
    // },
    alertInfo(): AlertInfo | null {
      if (!this.isBillingLoaded) return null;

      if (!this.isBillingAllowed) {
        return ({
          message: this.$tc('message.billing_warning_billing_navigation_notallowed'),
          type: 'warning',
          color: 'orange',
          buttons: [],
        });
      }

      // billing data not loaded -> no alert info yet
      if (!this.isBillingLoaded || this.isInitialOrder || !this.smsProduct) return null;

      // failed payment (todo!)
      if (this.smsProduct.latestInvoiceError) {
        return ({
          message: this.$tc('error.payment_latest_invoice_error'),
          type: 'error',
          color: 'red',
          buttons: [
            {
              text: this.$tc('button.show_billing'),
              color: 'error',
              action: () => { this.$router.push('/settings/billing'); },
            },
          ],
        });
      }

      // missing payment (open invoice) (todo!)
      if (this.smsProduct.latestInvoiceStatus === InvoiceStatus.Open) {
        return ({
          message: this.$tc('error.payment_invoice_not_closed_pay_invoice'),
          type: 'warning',
          color: 'orange',
          buttons: [
            {
              text: this.$tc('button.show_billing'),
              color: 'orange',
              action: () => { this.$router.push('/settings/billing'); },
            },
          ],
        });
      }

      // zero balance
      if (this.smsProduct.balance === 0) {
        return ({
          message: this.$tc('message.sms_credits_depleted_please_renew'),
          type: 'error',
          color: 'red',
          buttons: [],
        });
      }

      // low balance
      if (this.smsProduct.balance && this.smsProduct.balance < 10) {
        return ({
          message: this.$t('message.sms_low_credits_warning', [this.smsProduct!.balance]) as string,
          type: 'warning',
          color: 'orange',
          buttons: [],
        });
      }

      return null;
    },
    smsProduct(): SMSProduct | null {
      if (!this.isBillingLoaded) return null;

      const smsProduct = this.$tstore.getters.billingSMSProduct as SMSProduct | null;
      console.log('smsProduct: ', smsProduct);
      return smsProduct;
    },
    customer(): Customer | null {
      return this.$tstore.getters.billingCustomer;
    },
    currencySelectorEnabled(): boolean {
      return this.customer?.currency === undefined;
    },
    currencyText(): string {
      return billingCurrencyText(this.selectedCurrency);
    },
  },
  created() {
    loadStoreModule(this, 'billing', BillingStore, async () => { this.load(); });
  },
  mounted() {
  },
  async beforeDestroy() {
    unloadStoreModule(this, 'billing', async () => { this.$tstore.dispatch('resetBilling'); });
    await performAction(
      null,
      this.$i18n.tc('error.load_data'),
      async () => this.$tstore.dispatch('update'),
    );
  },
  methods: {
    async load() {
      await performAction(
        null,
        this.$i18n.tc('error.load_data'),
        async () => this.$tstore.dispatch('loadBilling', { force: true }),
      );

      await this.$nextTick();

      const customer = this.$tstore.getters.billingCustomer as Customer | null;
      this.selectedCurrency = customer?.currency ?? this.$tstore.getters.billingGuessedCurrency;

      const { account } = this.$tstore.state.settings;
      this.sender = this.smsProduct?.sender ?? this.sanitizeSmsSenderId(account.accountName) ?? '';

      const firstPriceID = this?.prices?.find(Boolean)?.ID ?? null;
      if (this.isInitialOrder) {
        this.autorenewPriceID = firstPriceID;
      } else {
        this.autorenewPriceID = this.smsProduct?.autorenewPriceID ?? null;
        // fix autorenew price id
        if (this.autorenewPriceID && !this.prices.find((p) => p.ID === this.autorenewPriceID)) {
          this.autorenewPriceID = firstPriceID;
        }
      }

      this.selectedPriceID = this.autorenewPriceID ?? firstPriceID;
      this.balanceNotificationThreshold = this.smsProduct?.balanceNotificationThreshold ?? 100;
      this.balanceNotificationEnabled = this.smsProduct?.balanceNotificationEnabled ?? false;
    },
    async startPayment() {
      let priceID = this.selectedPriceID;

      // initial payment with autorenew price id if enabled
      if (this.isInitialOrder && this.autorenewPriceID) priceID = this.autorenewPriceID;

      // save form data before payment
      if (this.isInitialOrder || this.isChanged) {
        const ok = await this.save();
        if (!ok) return;
      }

      // open billing wizard
      this.paymentWizardPrices = this.prices?.filter((p) => p.ID === priceID) ?? [];
      this.paymentWizardVisible = true;
    },
    async save(): Promise<boolean> {
      const isValid = await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate();
      if (!isValid) return false;

      const smsProduct: SMSProduct = {
        sender: this.sender.trim(),
        autorenewPriceID: this.autorenewPriceID ?? undefined,
        balanceNotificationThreshold: this.balanceNotificationThreshold,
        balanceNotificationEnabled: this.balanceNotificationEnabled,
      };

      const action = this.smsProduct ? 'billingUpdateSMSProduct' : 'billingCreateSMSProduct';
      const ok = await performSaveAction(
        this.$refs.observer,
        async () => this.$tstore.dispatch(action, { smsProduct }),
      );

      return ok;
    },
    autorenewChanged(value: boolean) {
      if (!value) {
        this.autorenewPriceID = null;
        this.balanceNotificationThreshold = this.smsProduct?.balanceNotificationThreshold ?? 100;
        this.balanceNotificationEnabled = true;
      } else {
        const firstPriceID = this?.prices?.find(Boolean)?.ID ?? null;
        this.autorenewPriceID = this.smsProduct?.autorenewPriceID ?? null;
        // fix autorenew price id
        if (!this.autorenewPriceID || !this.prices.find((p) => p.ID === this.autorenewPriceID)) {
          this.autorenewPriceID = firstPriceID;
        }
        this.balanceNotificationEnabled = false;
      }
    },
    closePaymentWizard() {
      this.paymentWizardVisible = false;
      this.load();
    },
    setCurrency(curr: string) {
      const api = this.autorenewPriceID ? this.prices.findIndex((p) => p.ID === this.autorenewPriceID) : -1;
      const spi = this.selectedPriceID ? this.prices.findIndex((p) => p.ID === this.selectedPriceID) : -1;
      this.selectedCurrency = curr;
      this.autorenewPriceID = api >= 0 && api < this.prices.length ? this.prices[api].ID! : null;
      this.selectedPriceID = spi >= 0 && spi < this.prices.length ? this.prices[spi].ID! : null;
    },
    sanitizeSmsSenderId(input?: string): string {
      if (!input) return '';
      let sanitized = input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      sanitized = sanitized.replace(/[^a-zA-Z0-9-_]/g, ''); // replace non-alpha
      sanitized = sanitized.replace(/^[^a-zA-Z]+/, ''); // remove leading if non letter

      return sanitized.slice(0, 11).padEnd(2, 'G'); // make sure min length is met
    },
  },
});
</script>
